/**
 * Wave 29 Chunk 10p — GPS-required clocking modal.
 *
 * Wraps `requestPosition` from lib/geo with a non-dismissable modal that:
 *   • Auto-fires `navigator.geolocation.getCurrentPosition` on mount.
 *   • If the user denied permission OR the position fails, shows an
 *     instructional banner with a Retry button.
 *   • Calls `onCaptured({lat, lng, accuracy_m, ...})` when a position
 *     lands.
 *
 * Iter 231.5 (2026-05-25) — DON'T HARD-BLOCK. If GPS fails, surface a
 * "Clock without GPS" fallback button so the field tech is NEVER stuck.
 * The parent passes `onSkipped(reason)` which triggers a no-GPS POST
 * (server stamps it for HR to review). Better an inaccurate timestamp
 * than no timestamp at all.
 *
 * Used by `ClockPage.jsx` before every /clock POST.
 */
import { useCallback, useEffect, useState } from "react";
import { MapPin, AlertTriangle, Loader2, ShieldCheck, FastForward } from "lucide-react";
import { requestPosition } from "../lib/geo";

export default function GpsRequiredModal({
  open,
  title = "Location required",
  reason = "Clocking events must include a GPS pin so the time-and-attendance register can verify where you clocked in/out.",
  onCaptured,
  onCancel,           // owner-supplied — when null, no cancel path is shown
  onSkipped,          // Iter 231.5 — confirmed fallback (no-GPS clock)
  highAccuracy = true,
  timeoutMs = 12000,
}) {
  const [state, setState] = useState({ phase: "asking", error: null });

  const fetchNow = useCallback(async () => {
    setState({ phase: "asking", error: null });
    const loc = await requestPosition({ timeoutMs, highAccuracy });
    if (loc.denied || loc.lat == null || loc.lng == null) {
      setState({ phase: "denied", error: loc.error || "unknown" });
      return;
    }
    setState({ phase: "captured", error: null });
    onCaptured?.(loc);
  }, [timeoutMs, highAccuracy, onCaptured]);

  useEffect(() => {
    if (open) fetchNow();
  }, [open, fetchNow]);

  if (!open) return null;

  const failed = state.phase === "denied";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 hv-safe-top hv-safe-bottom hv-safe-x"
      data-testid="gps-required-modal"
    >
      <div className="w-full max-w-md bg-[#121419] border border-white/10 rounded-t-2xl md:rounded-lg p-5 space-y-4">
        <header className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#1FB6C1]" />
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        </header>
        <p className="text-xs text-[#a0aabf] leading-relaxed">{reason}</p>

        {state.phase === "asking" && (
          <div className="border border-white/10 rounded p-3 flex items-center gap-2 text-xs text-[#a0aabf]" data-testid="gps-asking">
            <Loader2 className="w-4 h-4 animate-spin text-[#1FB6C1]" />
            Waiting for your browser to share location…
          </div>
        )}

        {state.phase === "captured" && (
          <div className="border border-emerald-500/30 bg-emerald-500/10 rounded p-3 flex items-center gap-2 text-xs text-emerald-300" data-testid="gps-captured">
            <ShieldCheck className="w-4 h-4" /> Location captured.
          </div>
        )}

        {failed && (
          <div className="border border-rose-500/40 bg-rose-500/10 rounded p-3 space-y-2" data-testid="gps-denied">
            <div className="flex items-start gap-2 text-xs text-rose-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {state.error === "permission_denied" ? (
                  <>
                    <b>Location blocked.</b> Open your browser settings and
                    allow location for this site, then tap "Retry". On Chrome
                    Android: tap the lock icon in the address bar → Permissions →
                    Location → Allow.
                  </>
                ) : state.error === "geolocation_timeout" ? (
                  <><b>Took too long to get a fix.</b> Move into the open and tap Retry — or clock without GPS if you can't get outside.</>
                ) : state.error === "geolocation_unsupported" ? (
                  <>Your browser doesn't expose location — please use a recent Chrome / Safari, or clock without GPS.</>
                ) : (
                  <><b>Couldn't capture GPS.</b> Step outside or near a window and retry — or clock without GPS.</>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Iter 231.5 — "Clock without GPS" fallback panel. Only appears
            on failure so we never tempt the tech to skip GPS when it
            would have worked. Two-tap confirm — first tap reveals the
            confirmation, second tap commits — to avoid accidental skips. */}
        {failed && onSkipped && (
          <SkipPanel
            error={state.error}
            onSkipped={onSkipped}
            data-testid="gps-skip-panel"
          />
        )}

        <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
          {onCancel && state.phase !== "captured" && (
            <button
              type="button"
              onClick={() => onCancel(state.error || state.phase || "cancelled")}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="gps-cancel"
            >
              Cancel
            </button>
          )}
          {failed && (
            <button
              type="button"
              onClick={fetchNow}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="gps-retry"
            >
              <MapPin className="w-3.5 h-3.5" /> Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SkipPanel({ error, onSkipped }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div
      className="border border-amber-400/40 bg-amber-400/5 rounded p-3 space-y-2"
      data-testid="gps-skip-panel"
    >
      <div className="text-[11px] text-amber-100 leading-relaxed">
        <b>Clock without GPS</b> — your event will still be recorded with
        the current time, but without a location pin. HR will see it
        flagged as "no GPS" and may follow up.
      </div>
      {confirmed && (
        <div className="text-[11px] text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded px-2 py-1.5">
          Tap "Yes — clock anyway" to confirm.
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        {!confirmed ? (
          <button
            type="button"
            onClick={() => setConfirmed(true)}
            className="hv-btn hv-btn-ghost text-xs text-amber-200 hover:!text-amber-100"
            data-testid="gps-skip-btn"
          >
            <FastForward className="w-3.5 h-3.5" /> Clock without GPS
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setConfirmed(false)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="gps-skip-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSkipped?.(error || "unknown")}
              className="hv-btn text-xs bg-amber-500/80 hover:bg-amber-500 text-white border border-amber-500/60"
              data-testid="gps-skip-confirm"
            >
              Yes — clock anyway
            </button>
          </>
        )}
      </div>
    </div>
  );
}
